import {
  CARGA_PARCIAL,
  GENERO,
  MESA_ESTADO,
  MESA_TIPO,
  PARTICIPANTE_ESTADO,
  RESULTADO_MOTIVO,
  RESULTADO_TIPO,
  RONDA_ESTADO,
  SANCION_TIPO,
  TORNEO_ESTADO,
  TORNEO_MODALIDAD,
} from '@domino/schema'
import { z } from 'zod'

/**
 * Lo que el jugador puede ver, definido una sola vez.
 *
 * El API lo produce y el site lo consume, así que las dos puntas tienen que
 * entender exactamente lo mismo. Vive acá y no en cada uno para que no puedan
 * separarse en silencio: si el API deja de mandar un campo, lo que falla es la
 * validación en el borde —con el nombre del campo— y no una pantalla que muestra
 * «undefined» tres clicks más adentro.
 *
 * Son esquemas de zod y no solo tipos por el mismo motivo. Un tipo desaparece al
 * compilar y no protege de nada en el momento que importa, que es cuando un
 * servidor de una versión distinta contesta algo parecido pero no igual.
 *
 * Todas las formas vienen con el punto de vista del jugador ya resuelto: la mesa
 * llega partida en compañeros y rivales, el marcador en puntos propios y ajenos.
 * Ordenar eso del lado del navegador obligaría a cada pantalla a averiguar de qué
 * bando está sentado, que es justo lo que no se puede equivocar.
 */

const uuid = z.string().uuid()
/** Día calendario, sin hora: el torneo se juega un día, no en un instante. */
const dia = z.string().regex(/^\d{4}-\d{2}-\d{2}$/)
/** Instante ISO-8601 en UTC. */
const instante = z.string().datetime()
const entero = z.number().int()
const noNegativo = z.number().int().nonnegative()
const positivo = z.number().int().positive()

/** Quien está mirando la página. */
export const jugadorSchema = z.object({
  id: uuid,
  nombre: z.string(),
  apodo: z.string().nullable(),
  /** El número con el que se lo nombra en el salón, el de su club. */
  numero: positivo.nullable(),
  pais: z.string().nullable(),
  genero: z.enum(GENERO).nullable(),
  /** Con lo que ingresó. Es la llave de su cuenta: el email ya no lo es. */
  usuario: z.string(),
  /** De contacto, y puede estar vacío: hay quien juega y no dejó ninguno. */
  email: z.string(),
  /** El club del que es. Lo que ve es lo suyo, y lo suyo pasa ahí adentro. */
  organizacion: z.string(),
})
export type Jugador = z.infer<typeof jugadorSchema>

/** Su línea de la tabla de posiciones, en un torneo que ya arrancó. */
export const miLineaSchema = z.object({
  participanteId: uuid,
  /** Nulo solo en Parejas, donde la línea es de la pareja. */
  torneoJugadorId: uuid.nullable(),
  posicion: positivo,
  ganadas: noNegativo,
  perdidas: noNegativo,
  efectividad: entero,
  puntosFavor: noNegativo,
  /** True si su lugar frente al de arriba lo definió el sorteo, no los criterios. */
  resueltoPorSorteo: z.boolean(),
  /** Un retirado sigue en la tabla: las rondas que jugó cuentan. */
  estado: z.enum(PARTICIPANTE_ESTADO),
})
export type MiLinea = z.infer<typeof miLineaSchema>

/** Uno de los dos de una pareja, tal como lo ve el que juega. */
export const integranteDeParejaSchema = z.object({
  nombre: z.string(),
  pais: z.string().nullable(),
  /** True en el que mira: es a quien la pantalla marca con «tú». */
  esMio: z.boolean(),
})
export type IntegranteDePareja = z.infer<typeof integranteDeParejaSchema>

/**
 * Su pareja, en un torneo de esa modalidad.
 *
 * Es nómina y no tabla, y por eso existe desde la inscripción y no desde el
 * inicio, al revés que `mi`: el que se anotó de a dos quiere ver con quién y
 * con qué número el día antes, que es cuando se lo pregunta. De las personas
 * salen las columnas de siempre: nombre y bandera, y nada de la cuenta.
 *
 * El número es el de la PAREJA, que en esta modalidad es el único que se
 * muestra: el que se grita en el salón, el que va en la hoja de mesa y el que
 * sale en la proyección. El de la ficha de cada persona no se usa acá.
 */
export const miParejaSchema = z.object({
  numero: positivo,
  /**
   * Los dos, por nombre. Puede venir uno solo: pasa cuando al compañero lo sacó
   * una roja antes de arrancar y la pareja quedó coja.
   */
  integrantes: z.array(integranteDeParejaSchema),
  /** Si los dos hicieron el check-in. Recién ahí entran al sorteo. */
  confirmada: z.boolean(),
})
export type MiPareja = z.infer<typeof miParejaSchema>

/**
 * Una tarjeta que le sacaron en ese torneo.
 *
 * Solo las propias, y por eso el motivo viaja: es lo que el árbitro escribió al
 * sancionarlo, y el jugador expulsado que abre la web merece leer por qué, no
 * encontrarse con su torneo cortado sin explicación. A los demás no les llega
 * nada de esto salvo la marca de expulsión en la tabla (`expulsion`), que es
 * lo mismo que se ve en la planilla del salón.
 */
export const miSancionSchema = z.object({
  tipo: z.enum(SANCION_TIPO),
  /** La ronda en la que se aplicó. Nula si fue antes de arrancar el torneo. */
  rondaNumero: positivo.nullable(),
  motivo: z.string(),
  /** Cuándo se aplicó, en ISO. */
  aplicadaAt: z.string(),
  /** Hasta cuándo sigue pesando. Solo la negra lo tiene, y siempre. */
  venceAt: z.string().nullable(),
})
export type MiSancion = z.infer<typeof miSancionSchema>

/**
 * Las dos tarjetas que sacan del torneo, las únicas que se ven en la tabla. Son
 * las de `SANCION_EXPULSA`, escritas acá como literales para que el tipo las
 * nombre a las dos y no a la escala entera.
 */
const expulsionSchema = z.enum(['roja', 'negra'])

/**
 * Un torneo en el que está anotado, con su situación adentro.
 *
 * `mi` viene nulo mientras el torneo no arrancó: antes del inicio no hay
 * participante, y por lo tanto no hay posición que mostrar.
 */
export const miTorneoSchema = z.object({
  id: uuid,
  nombre: z.string(),
  sede: z.string().nullable(),
  fecha: dia,
  modalidad: z.enum(TORNEO_MODALIDAD),
  estado: z.enum(TORNEO_ESTADO),
  numeroRondas: positivo,
  objetivoPuntos: positivo,
  /** Cuántos compiten: participantes, que en Parejas es la mitad de la gente. */
  participantes: noNegativo,
  /** Rondas ya cerradas: las que cuentan para la tabla. */
  rondasCerradas: noNegativo,
  /** Número de la ronda que se está jugando, si hay alguna abierta. */
  rondaEnCurso: positivo.nullable(),
  mi: miLineaSchema.nullable(),
  /**
   * Con quién juega, en Parejas. Nula en las otras modalidades, y en Parejas
   * cuando quedó suelto: su pareja se dio de baja y todavía no lo armaron con
   * nadie, así que no hay número ni compañero que mostrar.
   */
  pareja: miParejaSchema.nullable(),
  /** Sus tarjetas en este torneo, de la primera a la última. Casi siempre vacía. */
  sanciones: z.array(miSancionSchema),
  /** Si en este torneo las mesas se cargan mano a mano. Ver `CARGA_PARCIAL`. */
  cargaParcial: z.enum(CARGA_PARCIAL),
  /**
   * Si es un match, cuántas partidas gana: dos parejas, una mesa por ronda, y
   * se termina cuando una llega. `null` en un torneo de los de siempre.
   */
  partidasAGanar: positivo.nullable().default(null),
  /**
   * Cuándo se arma la ronda siguiente, en el torneo sin organizador con pausa
   * entre rondas. Nulo si no hay ninguna pausa corriendo. El teléfono cuenta
   * hacia atrás contra esto; quien arma la ronda al llegar a cero es el
   * servidor.
   */
  proximaRondaEn: instante.nullable().default(null),
})
export type MiTorneo = z.infer<typeof miTorneoSchema>

/** Alguien sentado a la mesa, visto desde la silla del que mira. */
export const sillaSchema = z.object({
  participanteId: uuid,
  nombre: z.string(),
  numero: positivo.nullable(),
  pais: z.string().nullable(),
  esMio: z.boolean(),
})
export type Silla = z.infer<typeof sillaSchema>

/** El marcador de su mesa, ya orientado: propios contra ajenos. */
export const miMarcadorSchema = z.object({
  puntosPropios: noNegativo,
  puntosRivales: noNegativo,
  resultado: z.enum(RESULTADO_TIPO),
  motivo: z.enum(RESULTADO_MOTIVO),
  /**
   * Lo que esa partida le sumó —o le restó— de efectividad, con los puntos ya
   * truncados al objetivo. Viaja calculado porque es el número que explica la
   * tabla y no puede salir distinto en el celular que en el salón.
   */
  efectividad: entero,
})
export type MiMarcador = z.infer<typeof miMarcadorSchema>

/**
 * Cómo va una partida que se está cargando mano a mano, en corto.
 *
 * Es lo que entra en la tarjeta de una mesa de la lista: el marcador parcial y
 * cuántas manos lleva. El detalle de cada mano viaja aparte (`PartidaEnVivo`),
 * porque con cien mesas por ronda traerlo en la lista sería bajar mil manos
 * para mirar una.
 */
export const parcialSchema = z.object({
  bando1: noNegativo,
  bando2: noNegativo,
  manos: noNegativo,
})
export type Parcial = z.infer<typeof parcialSchema>

/**
 * Una mesa suya, de una ronda cualquiera del torneo.
 *
 * Es la misma forma para la que está por jugar y para las que ya jugó: son lo
 * mismo mirado en dos momentos, y lo único que cambia es si `marcador` está
 * cargado.
 */
export const miMesaSchema = z.object({
  /** Con esto se pide y se carga la partida mano a mano. */
  mesaId: uuid,
  rondaId: uuid,
  rondaNumero: positivo,
  rondaEstado: z.enum(RONDA_ESTADO),
  mesaNumero: positivo,
  tipo: z.enum(MESA_TIPO),
  objetivoPuntos: positivo,
  /** Nulo en la mesa incompleta: sin rival no hay bandos. */
  miBando: z.union([z.literal(1), z.literal(2)]).nullable(),
  yo: sillaSchema,
  companeros: z.array(sillaSchema),
  rivales: z.array(sillaSchema),
  marcador: miMarcadorSchema.nullable(),
  /** El marcador parcial, si la mesa se está cargando mano a mano. Por bando, sin orientar. */
  parcial: parcialSchema.nullable(),
  /**
   * Cuándo se acaba el tiempo de la ronda, en los torneos sin organizador con
   * reloj. Nulo en la ronda sin límite. El teléfono cuenta hacia atrás contra
   * esto; quien cierra las mesas al llegar a cero es el servidor.
   */
  venceEn: instante.nullable().default(null),
})
export type MiMesa = z.infer<typeof miMesaSchema>

/** Una fila de la tabla del torneo. */
export const posicionPublicaSchema = z.object({
  posicion: positivo,
  /** La silla en la que juega, o jugaba, quien ocupa esta fila. */
  participanteId: uuid,
  /**
   * La persona, que es de quien es la fila.
   *
   * Nulo solo en Parejas, donde la unidad que compite es la pareja. Después de
   * una sustitución el titular y su suplente son dos filas distintas, con la
   * misma silla y distinta persona: cada uno ve lo que jugó.
   */
  torneoJugadorId: uuid.nullable(),
  nombre: z.string(),
  numero: positivo.nullable(),
  pais: z.string().nullable(),
  /**
   * Por qué equipo juega, o nulo fuera de la modalidad Equipos.
   *
   * El id además del nombre porque es con lo que la tabla agrupa a la gente bajo
   * la fila de su equipo, y dos equipos del mismo torneo pueden llamarse igual.
   */
  equipoId: uuid.nullable(),
  equipoNombre: z.string().nullable(),
  ganadas: noNegativo,
  perdidas: noNegativo,
  efectividad: entero,
  puntosFavor: noNegativo,
  resueltoPorSorteo: z.boolean(),
  estado: z.enum(PARTICIPANTE_ESTADO),
  /**
   * La tarjeta que lo sacó del torneo, o nulo si no lo sacó ninguna.
   *
   * Es lo que separa al expulsado del que se retiró solo: los dos quedan
   * `retirado`, y en la tabla no pueden verse igual. Sin el motivo, que es del
   * jugador y no del público. En Parejas es la de cualquiera de los dos: con uno
   * expulsado la pareja entera deja de jugar.
   */
  expulsion: expulsionSchema.nullable(),
  /** True en su fila: es la que la pantalla resalta y a la que se desplaza. */
  esMio: z.boolean(),
})
export type PosicionPublica = z.infer<typeof posicionPublicaSchema>

/**
 * Una fila de la tabla de equipos, en un torneo de esa modalidad.
 *
 * Es la clasificación de la modalidad —el puntaje del equipo es la suma de los de
 * sus miembros— y viaja aparte de la tabla de jugadores porque son dos listas de
 * alturas distintas, cada una con su propia numeración. En las otras modalidades
 * no hay equipos y la lista llega vacía.
 */
export const posicionEquipoPublicaSchema = z.object({
  posicion: positivo,
  equipoId: uuid,
  nombre: z.string(),
  /** Cuántos participantes le suman. Cero es un equipo sin nadie asignado. */
  miembros: noNegativo,
  ganadas: noNegativo,
  perdidas: noNegativo,
  efectividad: entero,
  puntosFavor: noNegativo,
  resueltoPorSorteo: z.boolean(),
  /** True en la fila de SU equipo: la que la pantalla resalta. */
  esMio: z.boolean(),
})
export type PosicionEquipoPublica = z.infer<typeof posicionEquipoPublicaSchema>

/**
 * Alguien sentado a una mesa del torneo, visto desde afuera.
 *
 * Es el hermano público de `Silla`. La diferencia no es de campos sino de punto
 * de vista: aquella llega ya repartida en «conmigo» y «en contra» porque el que
 * mira está sentado ahí, y esta trae el bando crudo porque la mesa que se mira
 * es casi siempre la de otros. `esMio` sigue estando, y es lo que permite
 * encontrar la propia mesa en una lista de cien.
 */
export const jugadorEnMesaSchema = z.object({
  participanteId: uuid,
  nombre: z.string(),
  numero: positivo.nullable(),
  pais: z.string().nullable(),
  /** El bando en el que se sienta. Nulo en la mesa incompleta, que no tiene. */
  bando: z.union([z.literal(1), z.literal(2)]).nullable(),
  /**
   * Cómo terminó la partida PARA ÉL, o nulo mientras la mesa no se cargó.
   *
   * Va por persona y no por bando porque así lo guarda el salón: si uno no se
   * presenta, los otros tres ganan y solo el ausente registra la derrota. Un
   * marcador por bando no podría contar eso.
   */
  resultado: z.enum(RESULTADO_TIPO).nullable(),
  motivo: z.enum(RESULTADO_MOTIVO).nullable(),
  /** True si es el que mira: es la mesa que la pantalla marca como suya. */
  esMio: z.boolean(),
})
export type JugadorEnMesa = z.infer<typeof jugadorEnMesaSchema>

/** El marcador de una mesa cualquiera, por bando y sin orientar. */
export const marcadorPublicoSchema = z.object({
  bando1: noNegativo,
  bando2: noNegativo,
  /** Cuál se llevó la partida. Nulo si no la ganó nadie: la mesa incompleta. */
  ganador: z.union([z.literal(1), z.literal(2)]).nullable(),
  motivo: z.enum(RESULTADO_MOTIVO),
})
export type MarcadorPublico = z.infer<typeof marcadorPublicoSchema>

/**
 * Una mesa de la ronda, con la gente que está jugando en ella.
 *
 * Es lo que el jugador ve cuando levanta la vista del propio marcador y mira el
 * salón: quién juega en la mesa 1, cómo va el que le gana en la tabla, dónde
 * quedó el amigo con el que vino. Es el mismo dato que está pegado en la pared,
 * y por eso alcanza con estar en la nómina del torneo para verlo.
 */
export const mesaPublicaSchema = z.object({
  mesaId: uuid,
  numero: positivo,
  tipo: z.enum(MESA_TIPO),
  /** `cargada` cuando ya tiene resultado; `pendiente` mientras se juega. */
  estado: z.enum(MESA_ESTADO),
  objetivoPuntos: positivo,
  /** En el orden en que se sientan, que es el que se ve en el salón. */
  jugadores: z.array(jugadorEnMesaSchema),
  marcador: marcadorPublicoSchema.nullable(),
  /**
   * Cómo va la partida, si en la mesa se cargó alguna mano. Nulo en los torneos
   * sin carga parcial y en la mesa que todavía no empezó a cargar.
   */
  parcial: parcialSchema.nullable(),
  /** True si el que mira juega en ella. */
  esMia: z.boolean(),
})
export type MesaPublica = z.infer<typeof mesaPublicaSchema>

/**
 * Una ronda del torneo, en la lista con la que se elige cuál mirar.
 *
 * Viaja sin las mesas a propósito: en un torneo de cuatrocientos son cien mesas
 * por ronda y nueve rondas, y traerlas todas para dibujar un selector de nueve
 * botones sería bajar cuatro mil nombres al teléfono para no mostrar ninguno.
 * Las mesas se piden de a una ronda, la que se está mirando.
 */
export const rondaPublicaSchema = z.object({
  rondaId: uuid,
  numero: positivo,
  estado: z.enum(RONDA_ESTADO),
  /** Cuántas mesas se armaron. Cero mientras la ronda no está emparejada. */
  mesas: noNegativo,
  /** Cuántas ya tienen resultado: es el avance de la ronda que se juega. */
  mesasCargadas: noNegativo,
})
export type RondaPublica = z.infer<typeof rondaPublicaSchema>

/**
 * Un torneo visto desde afuera, sin cuenta.
 *
 * Es lo que muestra la página pública: la que abre el que no juega, el familiar
 * que sigue el torneo desde casa, el socio de otro club que quiere ver cómo va.
 * Tiene los mismos datos del torneo que `MiTorneo` y ninguno del que mira: no
 * hay `mi` ni `pareja` porque no hay nadie mirando. Lo que agrega es el club,
 * porque la lista pública mezcla los torneos de todos los clubes del servidor.
 *
 * Solo salen los torneos con la inscripción abierta, en juego o terminados. El
 * borrador todavía no existe para el público y el cancelado ya no.
 */
export const torneoPublicoSchema = z.object({
  id: uuid,
  nombre: z.string(),
  sede: z.string().nullable(),
  fecha: dia,
  modalidad: z.enum(TORNEO_MODALIDAD),
  estado: z.enum(TORNEO_ESTADO),
  numeroRondas: positivo,
  objetivoPuntos: positivo,
  participantes: noNegativo,
  rondasCerradas: noNegativo,
  rondaEnCurso: positivo.nullable(),
  /** El club que lo organiza. */
  organizacion: z.string(),
  cargaParcial: z.enum(CARGA_PARCIAL),
  /** Si es un match, cuántas partidas gana. Ver `MiTorneo`. */
  partidasAGanar: positivo.nullable().default(null),
  /** Cuándo se arma la ronda siguiente. Ver `MiTorneo`. */
  proximaRondaEn: instante.nullable().default(null),
})
export type TorneoPublico = z.infer<typeof torneoPublicoSchema>

/**
 * Los estados que la vitrina deja pedir de a uno: cada uno es una pestaña de la
 * portada pública (En vivo, Próximos, Resultados).
 */
export const ESTADO_VITRINA = ['en_curso', 'inscripcion', 'finalizado'] as const
export type EstadoVitrina = (typeof ESTADO_VITRINA)[number]

/** Una mano ya jugada, con cómo quedó la partida después de ella. */
export const manoEnVivoSchema = z.object({
  numero: positivo,
  bandoGanador: z.union([z.literal(1), z.literal(2)]),
  puntos: positivo,
  /** El acumulado de cada bando después de esta mano. */
  bando1: noNegativo,
  bando2: noNegativo,
})
export type ManoEnVivo = z.infer<typeof manoEnVivoSchema>

/**
 * Puntos que el organizador le restó a un bando en plena partida, con cómo
 * quedó la partida después. Solo en los torneos de Parejas con la resta en vivo.
 */
export const restaEnVivoSchema = z.object({
  /** Cuántas manos había anotadas al restar. Cero: antes de la primera. */
  despuesDeMano: noNegativo,
  bando: z.union([z.literal(1), z.literal(2)]),
  puntos: positivo,
  /** A quién le sacaron la tarjeta, y por qué. */
  nombre: z.string(),
  tipo: z.enum(SANCION_TIPO),
  motivo: z.string(),
  bando1: noNegativo,
  bando2: noNegativo,
})
export type RestaEnVivo = z.infer<typeof restaEnVivoSchema>

/**
 * Una de las cuatro personas de la mesa, en el orden en que se juega (ver
 * `Silla` en el núcleo). Es por persona y no por participante: en Parejas el
 * participante son dos, y la salida la da uno solo.
 */
export const sillaEnVivoSchema = z.object({
  silla: z.number().int().min(1).max(4),
  bando: z.union([z.literal(1), z.literal(2)]),
  nombre: z.string(),
})
export type SillaEnVivo = z.infer<typeof sillaEnVivoSchema>

/**
 * Una partida mano a mano: lo que ve quien abre una mesa que se está jugando.
 *
 * La ven todos los que ven la mesa (la nómina del torneo y, si el torneo está
 * en la vitrina, cualquiera), y la misma forma sirve para los tres: lo único
 * que depende de quién mira es `puedoCargar`, que en la vitrina es siempre
 * false. El acumulado llega sumado por el API con la misma cuenta del núcleo
 * (`contarPartida`), así que el teléfono no suma nada.
 */
export const partidaEnVivoSchema = z.object({
  mesaId: uuid,
  mesaNumero: positivo,
  rondaNumero: positivo,
  objetivoPuntos: positivo,
  /** Cómo cuenta la carga en este torneo. En `desactivada` no hay manos. */
  modo: z.enum(CARGA_PARCIAL),
  /** `cargada` cuando la mesa ya tiene resultado oficial: ahí se congela. */
  mesaEstado: z.enum(MESA_ESTADO),
  /** En el orden en que se sientan, con su bando. */
  jugadores: z.array(jugadorEnMesaSchema),
  manos: z.array(manoEnVivoSchema),
  /** Las restas en vivo, en el orden en que entraron en la cuenta. */
  restas: z.array(restaEnVivoSchema).default([]),
  /**
   * Las cuatro personas en el orden en que se juega. Vacía en la mesa que no
   * tiene dos por bando: ahí no hay salida que anotar.
   */
  sillas: z.array(sillaEnVivoSchema).default([]),
  /**
   * La silla que dio la salida en la primera mano, o nulo si nadie la anotó. La
   * de cada mano siguiente no se guarda: rota (ver `quienSale`).
   */
  salida: z.number().int().min(1).max(4).nullable().default(null),
  /** Con las restas ya descontadas. */
  bando1: noNegativo,
  bando2: noNegativo,
  /** True cuando un bando llegó al objetivo. */
  concluida: z.boolean(),
  ganador: z.union([z.literal(1), z.literal(2)]).nullable(),
  /**
   * Si el que mira puede cargar o corregir manos ahora: está sentado ahí, el
   * torneo lo permite, la ronda no se cerró y la mesa no tiene resultado.
   */
  puedoCargar: z.boolean(),
  /**
   * Cuándo se acaba el tiempo de la ronda, o nulo si no tiene reloj. Pasado,
   * en la mesa empatada solo queda la mano de desempate.
   */
  venceEn: instante.nullable().default(null),
})
export type PartidaEnVivo = z.infer<typeof partidaEnVivoSchema>

/**
 * Una mano como la carga el jugador.
 *
 * `numero` es la mano que cree que está cargando (la siguiente a la última que
 * vio). No decide nada: sirve para que, si en la mesa dos teléfonos cargan la
 * misma mano a la vez, el segundo reciba «ya la cargaron» en lugar de dejar la
 * mano repetida.
 */
export const datosManoSchema = z.object({
  numero: positivo,
  bandoGanador: z.union([z.literal(1), z.literal(2)]),
  puntos: positivo,
})
export type DatosMano = z.infer<typeof datosManoSchema>

/** Una mano corregida: la mano la dice la ruta. */
export const correccionManoSchema = datosManoSchema.omit({ numero: true })
export type CorreccionMano = z.infer<typeof correccionManoSchema>

/** Quién dio la salida de la primera mano: una silla, o nulo para borrarla. */
export const datosSalidaSchema = z.object({
  silla: z.number().int().min(1).max(4).nullable(),
})
export type DatosSalida = z.infer<typeof datosSalidaSchema>

/**
 * Lo que avisa el socket de un torneo: QUÉ cambió, nunca cómo quedó.
 *
 * El cliente que lo recibe vuelve a pedir por la ruta de siempre lo que esté
 * mostrando. Mandar el marcador adentro del aviso abriría un segundo camino
 * para el mismo dato, y el día que se corrige uno solo los dos dicen cosas
 * distintas. Ver `docs/tiempo-real-pendiente.md`.
 *
 * - `mesa`: se anotó una mano o se cargó el resultado de esa mesa.
 * - `ronda`: se armó, se descartó o se cerró la ronda.
 * - `torneo`: cambió algo del torneo entero (arrancó, cambió la carga mano a
 *   mano, llegó un lote del salón): conviene releer todo.
 */
export const eventoTorneoSchema = z.discriminatedUnion('tipo', [
  z.object({ tipo: z.literal('mesa'), rondaId: uuid, mesaId: uuid }),
  z.object({ tipo: z.literal('ronda'), rondaId: uuid }),
  z.object({ tipo: z.literal('torneo') }),
])
export type EventoTorneo = z.infer<typeof eventoTorneoSchema>

/**
 * Lo que llega por el socket: los avisos, y uno más al principio, `listo`, que
 * dice que la suscripción quedó hecha. Es el momento en que el cliente relee
 * todo una vez: lo que cambió mientras no estaba conectado no se lo avisa nadie.
 */
export const mensajeSocketSchema = z.union([
  eventoTorneoSchema,
  z.object({ tipo: z.literal('listo') }),
])
export type MensajeSocket = z.infer<typeof mensajeSocketSchema>

/**
 * El primer mensaje del cliente: con qué sesión escucha, o ninguna (la vitrina).
 *
 * Va como mensaje y no en la URL porque la URL de un socket queda escrita en
 * los registros del proxy, y el token es la sesión del jugador. El navegador no
 * deja poner cabeceras en un WebSocket, así que el primer mensaje es el único
 * lugar limpio.
 */
export const saludoSocketSchema = z.object({ token: z.string().min(1).nullable() })
export type SaludoSocket = z.infer<typeof saludoSocketSchema>

/** Un torneo terminado, tal como queda en el historial. */
export const torneoJugadoSchema = z.object({
  id: uuid,
  nombre: z.string(),
  sede: z.string().nullable(),
  fecha: dia,
  modalidad: z.enum(TORNEO_MODALIDAD),
  participantes: noNegativo,
  posicion: positivo,
  ganadas: noNegativo,
  perdidas: noNegativo,
  efectividad: entero,
  puntosFavor: noNegativo,
})
export type TorneoJugado = z.infer<typeof torneoJugadoSchema>

/**
 * El acumulado de todo lo que jugó.
 *
 * Suma solo torneos finalizados: el que está en curso tiene su propia pantalla y
 * meterlo acá haría que el total cambiara mesa a mesa, que es lo contrario de lo
 * que un historial sirve para contar.
 */
export const resumenCarreraSchema = z.object({
  torneosJugados: noNegativo,
  partidasGanadas: noNegativo,
  partidasPerdidas: noNegativo,
  efectividad: entero,
  puntosFavor: noNegativo,
  /** El mejor puesto que consiguió. Nulo si todavía no terminó ninguno. */
  mejorPuesto: positivo.nullable(),
  podios: noNegativo,
  /** Del más nuevo al más viejo, que es como se lee un historial. */
  torneos: z.array(torneoJugadoSchema),
})
export type ResumenCarrera = z.infer<typeof resumenCarreraSchema>

/**
 * Lo que devuelve el ingreso.
 *
 * El token va en el cuerpo y no en una cookie: el site se sirve como archivos
 * estáticos y puede vivir en otro dominio que el API, donde una cookie de sesión
 * necesitaría CORS con credenciales y `SameSite=None` — más piezas para el mismo
 * resultado. Se guarda en el navegador y viaja en la cabecera.
 */
export const sesionSchema = z.object({
  token: z.string(),
  /** Cuándo deja de servir, en ISO. El site no lo usa para decidir: reacciona al 401. */
  expiraAt: z.string(),
  jugador: jugadorSchema,
})
export type Sesion = z.infer<typeof sesionSchema>
